import { useEffect, useRef, useState, type FormEvent } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { AdminSubscriptionRow } from '@/api/generated';
import { mapFieldErrors } from '@/api/fieldErrors';
import { ActionErrorAlert } from '@/components/ActionErrorAlert';
import { useActionError, type ActionError } from '@/components/useActionError';
import { SubmitButton } from '@/components/SubmitButton';
import { useFieldErrors } from '@/components/useFieldErrors';
import { useTenantApi } from '@/tenant/useTenant';

export interface PrefillSubscriber {
  userId: number;
  displayName?: string;
  email?: string;
}

export interface RequestDeletionDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (body: { userId: number; reason: string }) => Promise<void>;
  prefill?: PrefillSubscriber | null;
}

const REASON_MAX = 200;

const FIELDS = ['userId', 'reason'] as const;

/** The picker only needs the person. A prefill is not a full subscription row. */
type DeletionChoice = Pick<AdminSubscriptionRow, 'userId'> &
  Partial<Pick<AdminSubscriptionRow, 'id' | 'email' | 'displayName'>>;

function optionLabel(row: DeletionChoice): string {
  const name = row.displayName?.trim();
  const email = row.email?.trim();
  if (name && email) return `${name} (${email})`;
  return name || email || String(row.userId ?? row.id ?? '');
}

export function RequestDeletionDialog({
  open,
  onClose,
  onSubmit,
  prefill = null,
}: RequestDeletionDialogProps) {
  const { t } = useTranslation();
  const describe = useActionError();
  const userRef = useRef<HTMLInputElement>(null);
  const reasonRef = useRef<HTMLInputElement>(null);
  const [options, setOptions] = useState<AdminSubscriptionRow[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [selected, setSelected] = useState<DeletionChoice | null>(null);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<ActionError | null>(null);
  // The fields are disabled while submitting; `useFocusOnError` focuses after they are enabled again.
  const fields = useFieldErrors(FIELDS, { userId: userRef, reason: reasonRef }, submitting);
  const { reset: resetFields } = fields;
  const wasOpen = useRef(false);
  const api = useTenantApi();
  const prefillUserId = prefill?.userId;
  const prefillDisplayName = prefill?.displayName;
  const prefillEmail = prefill?.email;

  useEffect(() => {
    const opening = open && !wasOpen.current;
    wasOpen.current = open;
    if (!opening) return;

    setReason('');
    setError(null);
    resetFields();
    setSubmitting(false);
    if (prefillUserId !== undefined) {
      setSelected({
        userId: prefillUserId,
        ...(prefillDisplayName !== undefined ? { displayName: prefillDisplayName } : {}),
        ...(prefillEmail !== undefined ? { email: prefillEmail } : {}),
      });
      return;
    }
    setSelected(null);
    setLoadingOptions(true);
    api.listSubscriptions().then(
      (list) => {
        setOptions((list.subscriptions ?? []).filter((row) => row.userId !== undefined));
        setLoadingOptions(false);
      },
      () => {
        setOptions([]);
        setLoadingOptions(false);
      },
    );
  }, [api, open, prefillUserId, prefillDisplayName, prefillEmail, resetFields]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    const userId = selected?.userId;
    const trimmed = reason.trim();
    if (userId === undefined || trimmed.length === 0) {
      setError(null);
      fields.fail({
        ...(userId === undefined ? { userId: t('settings.data.subscriberRequired') } : {}),
        ...(trimmed.length === 0 ? { reason: t('settings.data.reasonRequired') } : {}),
      });
      return;
    }
    setSubmitting(true);
    setError(null);
    fields.reset();
    try {
      await onSubmit({ userId, reason: trimmed.slice(0, REASON_MAX) });
      onClose();
    } catch (err) {
      const action = describe(err);
      setError(action);
      fields.failMarked(mapFieldErrors(action.fieldKeys, { userId: 'userId', reason: 'reason' }), {
        userId: t('errors.fieldInvalid'),
        reason: t('settings.field.reason'),
      });
    } finally {
      setSubmitting(false);
    }
  }

  const hasPrefill = prefillUserId !== undefined;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} fullWidth maxWidth="sm">
      <Box component="form" noValidate onSubmit={(e) => void handleSubmit(e)}>
        <DialogTitle>{t('settings.data.requestTitle')}</DialogTitle>
        <DialogContent>
          <ActionErrorAlert error={error} sx={{ mb: 2 }} />
          <Typography sx={{ mb: 2 }}>{t('settings.data.requestBody')}</Typography>
          <Autocomplete
            options={hasPrefill ? (selected ? [selected] : []) : options}
            loading={loadingOptions}
            disabled={hasPrefill || submitting}
            value={selected}
            onChange={(_, value) => {
              setSelected(value);
              fields.clear('userId');
            }}
            getOptionLabel={optionLabel}
            isOptionEqualToValue={(a, b) => a.userId === b.userId}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="dense"
                required
                label={t('settings.data.subscriber')}
                inputRef={userRef}
                error={fields.errors.userId !== undefined}
                helperText={fields.errors.userId}
              />
            )}
          />
          <TextField
            margin="dense"
            fullWidth
            required
            multiline
            minRows={3}
            label={t('settings.data.reason')}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value.slice(0, REASON_MAX));
              fields.clear('reason');
            }}
            disabled={submitting}
            inputRef={reasonRef}
            error={fields.errors.reason !== undefined}
            helperText={
              fields.errors.reason ??
              t('settings.data.reasonCount', { count: reason.length, max: REASON_MAX })
            }
            slotProps={{ htmlInput: { maxLength: REASON_MAX } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <SubmitButton type="submit" color="error" loading={submitting}>
            {t('settings.data.requestAction')}
          </SubmitButton>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
